import React, { useState, useEffect, useRef } from "react";
import { Button } from "semantic-ui-react";
import { Modal, ModalBuilder } from "@redriver/cinnamon";
import { connect, useDispatch, useSelector } from "react-redux";
import { getSheetDataState } from "features/Sheets";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { updateLargeFormatGroupsOrder } from "features/Sheets/ManageSheet/ScheduleStep/actions";
import { useTranslation } from "react-i18next";

const LargeFormatReorderButton = () => {
  const [items, setItems] = useState([]);
  const modalRef = useRef(null);
  const [modalPosition, setModalPosition] = useState({ left: 0, top: 0 });

  const handleShowModal = (showModal) => {
    // Initialize the items state with the current systemScheduleItems
    setItems([...largeFormatGroups]);
    showModal();
  };

  // Handle the end of a drag operation
  const onDragEnd = (result) => {
    // Dropped outside the list
    if (!result.destination) {
      return;
    }

    // Reorder the items
    const reorderedItems = [...items];
    const [removed] = reorderedItems.splice(result.source.index, 1);
    reorderedItems.splice(result.destination.index, 0, removed);

    setItems(reorderedItems);
  };

  // Calculate modal position when it's mounted or window is resized
  useEffect(() => {
    const calculateModalPosition = () => {
      if (modalRef.current) {
        const rect = modalRef.current.getBoundingClientRect();
        setModalPosition({
          left: rect.left,
          top: rect.top,
        });
      }
    };

    // Set up an observer to detect when the modal appears
    if (typeof MutationObserver !== "undefined") {
      const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.addedNodes.length) {
            calculateModalPosition();
          }
        }
      });

      observer.observe(document.body, { childList: true, subtree: true });
      return () => observer.disconnect();
    }

    // Fallback to window resize events
    window.addEventListener("resize", calculateModalPosition);
    return () => window.removeEventListener("resize", calculateModalPosition);
  }, []);

  const dispatch = useDispatch();

  const { t } = useTranslation("productSchedule");

  const { largeFormatGroups } = useSelector(getSheetDataState);

  return (
    <ModalBuilder
      onSubmit={() => dispatch(updateLargeFormatGroupsOrder(items))}
      renderTrigger={(showModal) => {
        return largeFormatGroups.length > 0 ? (
          <Button primary onClick={() => handleShowModal(showModal)}>
            {t("Reorder_LFG_Button")}
          </Button>
        ) : null;
      }}
      renderModal={(modalProps) => (
        <Modal.Edit {...modalProps} header="Re-order Schedule">
          <div
            ref={modalRef}
            style={{ display: "flex", flexDirection: "column", gap: "10px" }}
          >
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="large-format-groups">
                {(provided) => (
                  <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "5px",
                    }}
                  >
                    {items.map((item, index) => (
                      <Draggable
                        key={item.id || index}
                        draggableId={String(item.id || index)}
                        index={index}
                      >
                        {(provided, snapshot) => {
                          // Apply dynamic offset based on actual modal position
                          if (snapshot.isDragging) {
                            provided.draggableProps.style = {
                              ...provided.draggableProps.style,
                              left:
                                provided.draggableProps.style.left -
                                modalPosition.left,
                              top:
                                provided.draggableProps.style.top -
                                modalPosition.top +
                                65,
                            };
                          }

                          return (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              style={{
                                ...provided.draggableProps.style,
                                backgroundColor: snapshot.isDragging
                                  ? "#81A7E3"
                                  : "#9FC5F8",
                                padding: "10px",
                                borderRadius: "5px",
                                boxShadow: snapshot.isDragging
                                  ? "0 5px 10px rgba(0, 0, 0, 0.2)"
                                  : "none",
                                cursor: "grab",
                              }}
                            >
                              <p>{item.printerName}</p>
                            </div>
                          );
                        }}
                      </Draggable>
                    ))}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>
        </Modal.Edit>
      )}
    />
  );
};

export default LargeFormatReorderButton;
